import type { RouteRecordRaw } from 'vue-router'
import { P } from '@shared/domain/permissions'
import { placeholder } from '../placeholder'

/** Stage 2 onboarding: shown in the workspace shell, reachable before activation. */
const onboarding: RouteRecordRaw = {
  path: 'onboarding',
  meta: {
    allowBeforeActivation: true,
    menu: { labelKey: 'menu.anchor.onboarding', icon: 'ClipboardList', order: 2 },
  },
  children: [
    {
      path: '',
      name: 'anchor.onboarding',
      redirect: { name: 'anchor.onboarding.step', params: { step: 'programme' } },
      meta: {
        permission: P.ANCHOR_ONBOARDING_EDIT,
        menu: { labelKey: 'menu.anchor.onboardingWizard' },
      },
    },
    {
      path: 'status',
      name: 'anchor.onboarding.status',
      component: placeholder,
      meta: { menu: { labelKey: 'menu.anchor.readiness' }, stage: '2' },
    },
    {
      path: ':step',
      name: 'anchor.onboarding.step',
      component: placeholder,
      meta: {
        permission: P.ANCHOR_ONBOARDING_EDIT,
        titleKey: 'menu.anchor.onboardingWizard',
        stage: '2',
      },
    },
  ],
}

const workspace: RouteRecordRaw = {
  path: '/anchor',
  component: () => import('@/layouts/WorkspaceLayout.vue'),
  meta: { workspace: 'anchor', requiresAuth: true },
  children: [
    { path: '', redirect: { name: 'anchor.dashboard' } },
    onboarding,
    {
      path: 'dashboard',
      name: 'anchor.dashboard',
      component: placeholder,
      meta: {
        menu: { labelKey: 'menu.dashboard', icon: 'LayoutDashboard', order: 1 },
        stage: 'All',
      },
    },
    {
      path: 'suppliers',
      meta: { menu: { labelKey: 'menu.anchor.suppliers', icon: 'Users', order: 3 } },
      children: [
        {
          path: '',
          name: 'anchor.suppliers',
          component: () => import('@ws/anchor/suppliers/views/SupplierDirectory.vue'),
          meta: { menu: { labelKey: 'menu.anchor.supplierDirectory' }, stage: '3' },
        },
        {
          path: 'nominate',
          name: 'anchor.suppliers.nominate',
          component: placeholder,
          meta: {
            permission: P.ANCHOR_SUPPLIER_NOMINATE,
            menu: { labelKey: 'menu.anchor.nominate' },
            stage: '3',
          },
        },
        {
          path: 'invitations',
          name: 'anchor.suppliers.invitations',
          component: placeholder,
          meta: {
            permission: P.ANCHOR_SUPPLIER_NOMINATE,
            menu: { labelKey: 'menu.anchor.invitations' },
            stage: '3',
          },
        },
      ],
    },
    {
      path: 'invoices',
      meta: { menu: { labelKey: 'menu.invoices', icon: 'FileText', order: 4 } },
      children: [
        {
          path: '',
          name: 'anchor.invoices',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.anchor.approvedInvoices' }, stage: '4' },
        },
        {
          path: 'batches',
          name: 'anchor.invoices.batches',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.anchor.intakeBatches' }, stage: '4' },
        },
        {
          path: 'ineligible',
          name: 'anchor.invoices.ineligible',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.ineligible' }, stage: '4' },
        },
      ],
    },
    {
      path: 'collections',
      meta: { menu: { labelKey: 'menu.collections', icon: 'CalendarClock', order: 5 } },
      children: [
        { path: '', redirect: { name: 'anchor.collections.calendar' } },
        {
          path: 'calendar',
          name: 'anchor.collections.calendar',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.anchor.calendar' }, stage: '8' },
        },
        {
          path: 'notices',
          name: 'anchor.collections.notices',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.anchor.notices' }, stage: '8' },
        },
        {
          path: 'settlements',
          name: 'anchor.collections.settlements',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.settlementStatus' }, stage: '8' },
        },
      ],
    },
    {
      path: 'integration',
      meta: { menu: { labelKey: 'menu.integration', icon: 'Plug', order: 6 } },
      children: [
        { path: '', redirect: { name: 'anchor.integration.logs' } },
        {
          path: 'connection',
          name: 'anchor.integration.connection',
          component: placeholder,
          meta: {
            permission: P.ANCHOR_INTEGRATION_MANAGE,
            menu: { labelKey: 'menu.anchor.erpConnection' },
            stage: '2',
          },
        },
        {
          path: 'mapping',
          name: 'anchor.integration.mapping',
          component: placeholder,
          meta: {
            permission: P.ANCHOR_INTEGRATION_MANAGE,
            menu: { labelKey: 'menu.anchor.messageMapping' },
            stage: '2',
          },
        },
        {
          path: 'logs',
          name: 'anchor.integration.logs',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.anchor.messageLogs' }, stage: '4' },
        },
      ],
    },
    {
      path: 'org',
      meta: { menu: { labelKey: 'menu.anchor.organisation', icon: 'Building2', order: 7 } },
      children: [
        { path: '', redirect: { name: 'anchor.org.profile' } },
        {
          path: 'profile',
          name: 'anchor.org.profile',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.anchor.profileProgrammes' }, stage: '2' },
        },
        {
          path: 'users',
          name: 'anchor.org.users',
          component: placeholder,
          meta: {
            permission: P.ANCHOR_USERS_MANAGE,
            menu: { labelKey: 'menu.usersRoles' },
            stage: '2',
          },
        },
        {
          path: 'documents',
          name: 'anchor.org.documents',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.documents' }, stage: '2' },
        },
      ],
    },
  ],
}

export const anchorRoutes: RouteRecordRaw[] = [workspace]
